import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

/**
 * Sign-in flow with optional TOTP 2FA gate.
 *
 *   1. User submits email+password.
 *   2. If the backend responds with `requires_2fa: true`, we hold the
 *      short-lived `challenge_token` in component state (never persisted
 *      — a fresh login is needed if the page is reloaded) and render the
 *      6-digit-OTP step.
 *   3. Submitting the OTP exchanges the challenge for a real session and
 *      we route to the dashboard exactly like the no-2FA path.
 *   4. A "Use a recovery code" link swaps the OTP form for a recovery
 *      code form (single-use, 10 hex chars).
 */
export default function Login() {
  const { login, login2fa, recoveryLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [challengeToken, setChallengeToken] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const data = await login(email, password);
      if (data?.requires_2fa) {
        setChallengeToken(data.challenge_token);
        setOtp(""); setRecoveryCode(""); setRecoveryMode(false);
      } else {
        nav(data.role === "client" ? "/client" : "/panelist");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const data = recoveryMode
        ? await recoveryLogin(challengeToken, recoveryCode.trim().toLowerCase())
        : await login2fa(challengeToken, otp.trim());
      nav(data.role === "client" ? "/client" : "/panelist");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const cancel2fa = () => {
    setChallengeToken(null); setOtp(""); setRecoveryCode(""); setError(null);
    setRecoveryMode(false);
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="login-page">
      <Navigation />
      <div className="max-w-md mx-auto px-6 py-16">
        {!challengeToken ? (
          <>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Welcome back</div>
            <h1 className="font-display font-black text-4xl tracking-tight mt-2">Sign in.</h1>
            <form onSubmit={submitPassword} className="mt-10 space-y-5" data-testid="login-form">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">Work / Personal Email</label>
                <input
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold">Password</label>
                  <Link to="/forgot-password" className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-brand)] hover:underline" data-testid="link-forgot-password">Forgot?</Link>
                </div>
                <input
                  type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                  data-testid="login-password-input"
                />
              </div>
              {error && <div className="text-sm text-[var(--rl-accent)] font-medium" data-testid="login-error">{error}</div>}
              <button
                type="submit" disabled={loading}
                className="w-full bg-[var(--rl-text)] text-white py-4 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <div className="mt-6 text-sm text-[var(--rl-text-secondary)]">
              New here?{" "}
              <Link to="/register/client" className="text-[var(--rl-brand)] font-semibold" data-testid="link-register-client">Register as a Client</Link>
              {" • "}
              <Link to="/register/panelist" className="text-[var(--rl-brand)] font-semibold" data-testid="link-register-panelist">Join as a Panelist</Link>
            </div>
          </>
        ) : (
          <div data-testid="login-2fa-step">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Two-Factor Authentication</div>
            <h1 className="font-display font-black text-4xl tracking-tight mt-2">
              {recoveryMode ? "Recovery code." : "Verify it's you."}
            </h1>
            <p className="text-sm text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
              {recoveryMode
                ? "Enter one of the one-time recovery codes you saved when you set up 2FA. Each code can only be used once."
                : "Open Google Authenticator, 1Password, or any TOTP app and enter the 6-digit code for Adrigo Insights AI."}
            </p>
            <form onSubmit={submitOtp} className="mt-8 space-y-5">
              {recoveryMode ? (
                <input
                  type="text" inputMode="text" autoComplete="one-time-code"
                  maxLength={16} required autoFocus
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="abcd12ef34"
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-4 text-2xl font-mono tracking-widest text-center focus:outline-none focus:border-[var(--rl-brand)]"
                  data-testid="login-recovery-input"
                />
              ) : (
                <input
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  pattern="[0-9]*" maxLength={6} required autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="000000"
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-4 text-3xl font-mono tracking-[0.5em] text-center focus:outline-none focus:border-[var(--rl-brand)]"
                  data-testid="login-otp-input"
                />
              )}
              {error && <div className="text-sm text-[var(--rl-accent)] font-medium" data-testid="login-error">{error}</div>}
              <button
                type="submit" disabled={loading}
                className="w-full bg-[var(--rl-text)] text-white py-4 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
                data-testid="login-2fa-submit-btn"
              >
                {loading ? "Verifying…" : "Verify"}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button" onClick={() => { setRecoveryMode(!recoveryMode); setError(null); }}
                  className="text-[var(--rl-brand)] font-semibold uppercase tracking-[0.2em]"
                  data-testid="login-toggle-recovery-mode"
                >
                  {recoveryMode ? "Use OTP instead" : "Use a recovery code"}
                </button>
                <button
                  type="button" onClick={cancel2fa}
                  className="text-[var(--rl-text-secondary)] font-semibold uppercase tracking-[0.2em]"
                  data-testid="login-cancel-2fa"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
